import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect
} from 'react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useLazyQuery } from '@apollo/client';
import { validate } from 'validate.js';
import {
  withErrorBoundary,
  getTranslatedText,
  reactSelectInlineError,
  COMMON_CONSTANTS
} from '@nile/components';
import { emailValidator } from '@nile/components/src/Validators/fields';
import {
  SEARCH_VENDOR_CONVERSATION_RECIPIENT,
  AccountClient
} from '@nile/graphql';
import {
  DisplayText,
  AsyncCreatableDropdown as AsyncCreatableSelect
} from '@superops/ui-design-system';

const SearchableEmailSelect = withErrorBoundary(
  injectIntl((props) => {
    const {
      intl,
      mailList = [],
      label,
      placeholder,
      updateChange,
      hidelabel,
      error,
      noBorder
    } = props;

    const [errorMap, setErrorMap] = useState(error || null);
    const [searchRecipients] = useLazyQuery(
      SEARCH_VENDOR_CONVERSATION_RECIPIENT,
      {
        client: AccountClient,
        fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NETWORK_ONLY
      }
    );

    const validateEmail = (val) => {
      const data = { email: val };
      validate.validators.email.message = getTranslatedText(
        intl,
        'common.validatejs.email.invalid.message'
      );
      const emailError = validate(data, {
        email: emailValidator({
          mandatory: true
        })
      });
      if (emailError) {
        setErrorMap(emailError?.email[0]);
        return false;
      }
      setErrorMap(null);
      return true;
    };

    const debounceTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);

    useEffect(() => {
      return () => {
        isMountedRef.current = false;
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, []);

    const loadOptions = useCallback(
      (inputValue) => {
        return new Promise((resolve) => {
          if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
          }

          debounceTimeoutRef.current = setTimeout(async () => {
            if (!isMountedRef.current) {
              resolve([]);
              return;
            }

            if (!inputValue || inputValue.length < 2) {
              resolve([]);
              return;
            }

            try {
              const { data } = await searchRecipients({
                variables: {
                  input: {
                    keyword: inputValue,
                    listInfo: {
                      page: 1,
                      pageSize: 20
                    }
                  }
                }
              });

              if (!isMountedRef.current) {
                resolve([]);
                return;
              }

              const userList =
                data?.searchVendorConversationRecipient?.userList || [];

              const results = userList
                .filter((user) => user.email)
                .map((user) => ({
                  label: `${user.name || user.email} ${
                    user.email ? `<${user.email}>` : ''
                  }`,
                  value: user.email,
                  email: user.email,
                  userId: user.userId,
                  name: user.name
                }));

              resolve(results);
            } catch (error) {
              resolve([]);
            }
          }, 500);
        });
      },
      [searchRecipients]
    );

    const handleChange = (selectedOptions) => {
      const formattedList = (selectedOptions || []).map((option) => ({
        email: option.email || option.value,
        userId: option.userId,
        name: option.name
      }));
      updateChange(formattedList);
      setErrorMap(null);
    };

    const handleCreateOption = (inputValue) => {
      if (!validateEmail(inputValue)) {
        return;
      }
      const newOption = { email: inputValue };
      const updatedList = [...mailList, newOption];
      updateChange(updatedList);
    };

    const formatCreateLabel = (inputValue) => {
      return `${getTranslatedText(intl, 'common.create')} "${inputValue}"`;
    };

    const isValidNewOption = (inputValue) => {
      if (!inputValue) return false;
      const data = { email: inputValue };
      validate.validators.email.message = '';
      const emailError = validate(data, {
        email: emailValidator({ mandatory: true })
      });
      return !emailError;
    };

    const currentValue = mailList?.map((mail) => ({
      label: mail.name ? `${mail.name} <${mail.email}>` : mail.email,
      value: mail.email,
      email: mail.email,
      userId: mail.userId,
      name: mail.name
    }));

    return (
      <div className="fx select-wrapper full-width">
        <div className="flex center w100">
          {!hidelabel && label && (
            <span className="tw-mr-2">
              <DisplayText element="span" variation="subdued" size="medium">
                <FormattedMessage id={label} />
              </DisplayText>
            </span>
          )}
          <div className="w100">
            <AsyncCreatableSelect
              cacheOptions
              defaultOptions
              isMulti
              value={currentValue}
              noBorder
              loadOptions={loadOptions}
              onChange={handleChange}
              onCreateOption={handleCreateOption}
              formatCreateLabel={formatCreateLabel}
              isValidNewOption={isValidNewOption}
              placeholder={placeholder}
              noOptionsMessage={({ inputValue }) => {
                if (!inputValue)
                  return getTranslatedText(
                    intl,
                    'asset.details.fileExlporer.searchInput.placeholder'
                  );
                if (inputValue.length < 2)
                  return getTranslatedText(
                    intl,
                    'app.autocomplete.placeholder'
                  );

                return getTranslatedText(
                  intl,
                  'common.message.enterValidEmail'
                );
              }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              components={{
                IndicatorSeparator: () => null
              }}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>
          {errorMap && <div>{reactSelectInlineError(errorMap)}</div>}
        </div>
      </div>
    );
  })
);

export { SearchableEmailSelect };
